import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { ContractTypeEnum } from '../../../../resources/base/models';
import { isLvdlProduct } from '../../../../resources/base/models/financialProduct';
import { assertDefined } from '../../../../services/common/miscUtils';
import { DeepRequired } from '../../../../services/common/miscUtils.types';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { selectProductNameFromDataStorage } from '../../../../services/redux/features/dataStorage.redux';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import {
  AdditionalIncomeAmount,
  CityEmployer,
  ContractType,
  CountryCodeEmployer,
  DropdownForLVDL,
  Employer,
  EmploymentStartDate,
  MaritalStatus,
  MonthlyRent,
  NetIncomeAmount,
  NumberOfDependentChildren,
  OtherMonthlyCosts,
  ZipCodeEmployer,
} from '../../utils/fieldsPrivateCustomer';
import { getOccupationGroupFlags } from '../occupationBasedFieldsFilter';
import {
  CustomerDetailsFormGroupForms,
  getMainEmployment,
  patchCreditApplicationAndUpdateState,
  useFormSectionHelpers,
} from '../utils';
import { FinancialDataFormType, getFinancialDataFormInitialValues } from './initialValues';
import { financialDataFormRequestMapper } from './requestMapper';
import { getFinancialDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'private'>;
}

export const FinancialDataForm: FunctionComponent<Props> = ({ formName }) => {
  const [initialValues, setInitialValues] = useState({} as FinancialDataFormType);
  const [validationSchema, setValidationSchema] = useState<ObjectSchema<FinancialDataFormType>>();
  const creditApplicantCustomerData = useSelector(
    creditApplicationSelectors.selectCreditApplicantCustomerData
  ) as CreditIntegration.Schemas.UpdateIndividualCustomerDataResponse;
  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);
  const mainPosFrOccupation = getMainEmployment(creditApplicantCustomerData)?.posFrOccupation;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isCreditApplicationDataReady) return;

    setInitialValues(getFinancialDataFormInitialValues(mainPosFrOccupation!, creditApplicantCustomerData));
    setValidationSchema(getFinancialDataFormValidationSchema(mainPosFrOccupation!, t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData, isCreditApplicationDataReady]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    'private',
    formName
  );

  const onSubmit = async (values: FinancialDataFormType) => {
    dispatch(loadingActions.setLoading(true));
    const { isOccupationGroupA, isOccupationGroupB } = getOccupationGroupFlags(mainPosFrOccupation!);
    patchCreditApplicationAndUpdateState(
      financialDataFormRequestMapper(
        values as DeepRequired<FinancialDataFormType>,
        creditApplicantCustomerData,
        isOccupationGroupA,
        isOccupationGroupB
      )
    )
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:personalStatus')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent
              mainPosFrOccupation={mainPosFrOccupation!}
              onFormInitialValuesChange={onFormInitialValuesChange}
            />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  mainPosFrOccupation: CreditIntegration.Schemas.OccupationEnumValue;
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
}

const FormContent: FunctionComponent<FormContentProps> = ({ mainPosFrOccupation, onFormInitialValuesChange }) => {
  const { t } = useTranslation();
  const { values, setFieldValue, initialValues, validateForm, submitForm } = useFormikContext<FinancialDataFormType>();

  const productName = assertDefined(useSelector(selectProductNameFromDataStorage), 'productName');

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  const { isOccupationGroupA, isOccupationGroupB } = getOccupationGroupFlags(mainPosFrOccupation);

  const isLVDL = isLvdlProduct(productName);
  const showEmployerFields = !isOccupationGroupA && values.contractType !== ContractTypeEnum.NO_CONTRACT;

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          {isLVDL && (
            <>
              <Layout.Item default="1/2" s="1/1" />
              <Layout.Item default="1/2" s="1/1">
                <DropdownForLVDL t={t} />
              </Layout.Item>
            </>
          )}
          <Layout.Item default="1/5" m="1/3" s="1/1">
            <MaritalStatus t={t} />
          </Layout.Item>
          <Layout.Item default="1/5" m="1/6" s="1/1">
            <NumberOfDependentChildren t={t} />
          </Layout.Item>
          <Layout.Item default="1/3" m="1/2" s="1/1">
            <ContractType t={t} disabled={isOccupationGroupA} />
          </Layout.Item>
          {!isOccupationGroupB && (
            <Layout.Item default="1/4" m="1/2" s="1/1">
              <EmploymentStartDate
                t={t}
                setFieldValue={setFieldValue}
                values={values}
                infoText={t('step4:dateInfoText:label')}
                label={t('step4:employmentStartDate:label')}
              />
            </Layout.Item>
          )}
          {showEmployerFields && (
            <>
              <Layout.Item default="1/4" m="1/2" s="1/1">
                <Employer t={t} />
              </Layout.Item>
              <Layout.Item default="1/4" m="1/3" s="1/1">
                <ZipCodeEmployer t={t} />
              </Layout.Item>
              <Layout.Item default="1/4" m="1/3" s="1/1">
                <CityEmployer t={t} />
              </Layout.Item>
              <Layout.Item default="1/4" m="1/3" s="1/1">
                <CountryCodeEmployer t={t} />
              </Layout.Item>
            </>
          )}
          <Layout.Item default="1/4" m="1/4" s="1/1">
            <NetIncomeAmount t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/4" s="1/1">
            <AdditionalIncomeAmount t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/4" s="1/1">
            <MonthlyRent t={t} />
          </Layout.Item>
          <Layout.Item default="1/4" m="1/4" s="1/1">
            <OtherMonthlyCosts t={t} />
          </Layout.Item>
          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
